import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/Button';
import { CheckCircle, ArrowRight, Download, FileText, Loader2, Crown, Star, Zap } from 'lucide-react';
import { useTranslation } from '../lib/i18n';

const Success = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const isUpgrade = searchParams.get('upgraded') === 'true';
  const [loading, setLoading] = useState(true);
  const [purchaseDetails, setPurchaseDetails] = useState<any>(null);

  useEffect(() => {
    // Load purchase details and refresh subscription
    const timer = setTimeout(() => {
      setLoading(false);
      // In a real app, you'd fetch the session details from your backend
      setPurchaseDetails({
        productName: 'DocHelper AI',
        amount: '$19.99',
        sessionId: sessionId,
        type: 'subscription',
        features: [
          t('success.feat1'),
          t('success.feat2'),
          t('success.feat3'),
          t('success.feat4'),
          t('success.feat5'),
          t('success.feat6')
        ]
      });
      
      // Force refresh subscription data after successful payment
      console.log('🔄 Payment success page loaded, refreshing subscription data...');
      window.dispatchEvent(new CustomEvent('refreshSubscription'));
    }, 2000);

    
    // Multiple refresh attempts to ensure subscription is updated
    const refreshSubscription = () => {
      console.log('🔄 SUCCESS PAGE: Dispatching subscription refresh event...');
      console.log('🕐 SUCCESS PAGE: Refresh timestamp:', new Date().toISOString());
      window.dispatchEvent(new CustomEvent('refreshSubscription'));
    };
    
    // Multiple refresh attempts with different timing
    console.log('🔄 SUCCESS PAGE: Starting multiple refresh attempts...');
    refreshSubscription();
    
    // Staggered refreshes to handle webhook processing delays
    setTimeout(refreshSubscription, 2000);
    setTimeout(refreshSubscription, 5000);
    setTimeout(refreshSubscription, 10000);
    setTimeout(refreshSubscription, 15000);
    
    // Also refresh when the page becomes visible (in case user switched tabs)
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        console.log('🔄 SUCCESS PAGE: Page became visible, refreshing subscription...');
        refreshSubscription();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [sessionId, t]);

  if (loading) {
    return (
      <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center">
        <div className="text-center space-y-6 p-8 max-w-md w-full">
          <Loader2 className="w-16 h-16 text-blue-500 mx-auto animate-spin" />
          <h2 className="text-2xl font-bold">{t('success.processing')}</h2>
          <p className="text-gray-600 dark:text-gray-300">
            {t('success.processingDesc')}
          </p>
        </div>
      </div>
    );
  }

  const getProductIcon = () => {
    if (purchaseDetails?.productName?.includes('Team')) {
      return <Crown className="w-12 h-12 text-purple-600" />;
    }
    if (purchaseDetails?.productName?.includes('Basic')) {
      return <Star className="w-12 h-12 text-blue-600" />;
    }
    return <Zap className="w-12 h-12 text-green-600" />;
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center">
      <div className="text-center space-y-8 p-8 max-w-3xl w-full">
        <div className="relative">
          <div className="w-24 h-24 bg-green-100 dark:bg-green-900/50 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-12 h-12 text-green-600 dark:text-green-400" />
          </div>
          <div className="absolute -top-2 -right-2 w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center animate-bounce">
            <span className="text-white text-sm font-bold">✓</span>
          </div>
        </div>

        <div className="space-y-4">
          <h1 className="text-4xl font-bold bg-gradient-to-r from-green-600 to-blue-600 bg-clip-text text-transparent">
            {isUpgrade ? t('success.upgradeSuccessful') : t('success.paymentSuccessful')}
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-300">
            {isUpgrade
              ? t('success.upgradeMessage')
              : t('success.welcomeMessage')
            }
          </p>
        </div>

        {purchaseDetails && (
          <div className="bg-gradient-to-r from-green-50 to-blue-50 dark:from-gray-800 dark:to-gray-700 rounded-xl p-6 border border-green-200 dark:border-gray-600">
            <div className="flex items-center justify-center mb-4">
              {getProductIcon()}
            </div>
            <h3 className="text-lg font-bold mb-4">
              {purchaseDetails.productName} {t('success.subscription')}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-left">
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">{t('success.product')}</span>
                  <span className="font-medium">{purchaseDetails.productName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">{t('success.amount')}</span>
                  <span className="font-medium">{purchaseDetails.amount}/month</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">{t('success.type')}</span>
                  <span className="font-medium capitalize">{t('success.subscription')}</span>
                </div>
              </div>
              <div>
                <h4 className="font-medium mb-2">{t('success.newFeatures')}</h4>
                <ul className="text-sm space-y-1">
                  {purchaseDetails.features?.slice(0, 4).map((feature: string, index: number) => (
                    <li key={index} className="flex items-center">
                      <CheckCircle className="w-3 h-3 text-green-500 mr-2 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                  {purchaseDetails.features?.length > 4 && (
                    <li className="text-gray-500 text-xs">
                      {t('success.moreFeatures', { count: purchaseDetails.features.length - 4 })}
                    </li>
                  )}
                </ul>
              </div>
            </div>
            {purchaseDetails.sessionId && (
              <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-600">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600 dark:text-gray-400">{t('success.sessionId')}</span>
                  <span className="font-mono text-xs">{purchaseDetails.sessionId}</span>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="space-y-4">
          <h3 className="text-xl font-bold">{t('success.whatsNext')}</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md border border-gray-200 dark:border-gray-700">
              <FileText className="w-8 h-8 text-blue-500 mb-3" />
              <h4 className="font-bold mb-2">{t('success.startCreating')}</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                {t('success.startCreatingDesc')}
              </p>
              <Link to="/editor">
                <Button size="sm" className="w-full">
                  {t('success.createDocument')}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>

            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md border border-gray-200 dark:border-gray-700">
              <Download className="w-8 h-8 text-purple-500 mb-3" />
              <h4 className="font-bold mb-2">{t('success.premiumTemplates')}</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                {t('success.premiumTemplatesDesc')}
              </p>
              <Link to="/templates">
                <Button variant="outline" size="sm" className="w-full">
                  {t('success.browseTemplates')}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>

            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md border border-gray-200 dark:border-gray-700">
              <Crown className="w-8 h-8 text-orange-500 mb-3" />
              <h4 className="font-bold mb-2">{t('success.manageSubscription')}</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                {t('success.manageSubscriptionDesc')}
              </p>
              <Link to="/settings">
                <Button variant="outline" size="sm" className="w-full">
                  {t('success.viewSettings')}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>

        <div className="pt-6 border-t border-gray-200 dark:border-gray-700">
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            {t('success.confirmationEmail')}
          </p>
          <div className="flex gap-4 justify-center">
            <Link to="/dashboard">
              <Button variant="outline" className="font-bold">
                {t('success.returnDashboard')}
              </Button>
            </Link>
            <Link to="/editor">
              <Button className="font-bold">
                {t('success.startCreating')}
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Success;